import permissionsService, { PermissionSummary, Environment, Operation } from './permissions.service';

// In production, use same origin (empty string means relative URLs)
// In development, explicitly point to the backend server
const API_URL = window.location.origin.includes('localhost')
  ? 'http://localhost:5003'
  : ''; // Empty string for same-origin requests in production

export interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
  groups: string[];
}

export interface AuthStatus {
  authenticated: boolean;
  user?: User;
  permissions?: PermissionSummary;
}

class AuthService {
  /**
   * Check authentication status
   */
  async checkAuthStatus(): Promise<AuthStatus> {
    try {
      const response = await fetch(`${API_URL}/auth/status`, {
        credentials: 'include' // Important: include cookies
      });

      if (!response.ok) {
        return { authenticated: false };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to check auth status:', error);
      return { authenticated: false };
    }
  }

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await fetch(`${API_URL}/auth/user`, {
        credentials: 'include'
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.user;
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  /**
   * Initiate login - redirects to Azure AD
   */
  login(): void {
    const returnUrl = encodeURIComponent(window.location.origin);
    window.location.href = `${API_URL}/auth/login?returnUrl=${returnUrl}`;
  }

  /**
   * Logout - redirects to Azure AD logout
   */
  logout(): void {
    window.location.href = `${API_URL}/auth/logout`;
  }

  /**
   * Check if user has specific role
   */
  hasRole(user: User | null, role: string): boolean {
    return user?.roles?.includes(role) || false;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(user: User | null, roles: string[]): boolean {
    return roles.some(role => this.hasRole(user, role));
  }

  /**
   * Check if user belongs to specific Azure AD group
   */
  hasGroup(user: User | null, groupId: string): boolean {
    return user?.groups?.includes(groupId) || false;
  }

  /**
   * Check if user has WAD Admin Tools access
   * Now checks for any valid application role
   */
  hasWADAdminAccess(user: User | null): boolean {
    if (!user || !user.roles) return false;
    return permissionsService.hasValidRole(user.roles);
  }

  /**
   * Get user's permissions
   */
  async getUserPermissions(): Promise<PermissionSummary | null> {
    try {
      const response = await fetch(`${API_URL}/auth/permissions`, {
        credentials: 'include'
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to get permissions:', error);
      return null;
    }
  }

  /**
   * Check if user can access a domain
   */
  canAccessDomain(user: User | null, domainId: string): boolean {
    if (!user || !user.roles) return false;
    return permissionsService.canAccessDomain(user.roles, domainId);
  }

  /**
   * Get all domains user has access to
   */
  getUserDomains(user: User | null): string[] {
    if (!user || !user.roles) return [];
    return permissionsService.getUserDomains(user.roles);
  }

  /**
   * Get permissions for a specific domain
   */
  getDomainPermissions(user: User | null, domainId: string) {
    if (!user || !user.roles) {
      return {
        domain: domainId,
        canView: false,
        canModify: false,
        allowedEnvironments: []
      };
    }
    return permissionsService.getDomainPermissions(user.roles, domainId);
  }

  /**
   * Check if user can perform an operation on a domain/environment
   */
  canPerformOperation(
    user: User | null,
    domainId: string,
    environment: Environment,
    operation: Operation
  ): boolean {
    if (!user || !user.roles) return false;
    return permissionsService.canPerformOperation(user.roles, domainId, environment, operation);
  }

  /**
   * Check if user can access an environment in a domain
   */
  canAccessEnvironment(user: User | null, domainId: string, environment: Environment): boolean {
    if (!user || !user.roles) return false;
    return permissionsService.canAccessEnvironment(user.roles, domainId, environment);
  }

  /**
   * Check if user can modify an environment in a domain
   */
  canModifyEnvironment(user: User | null, domainId: string, environment: Environment): boolean {
    if (!user || !user.roles) return false;
    return permissionsService.canModifyEnvironment(user.roles, domainId, environment);
  }

  /**
   * Check if user can sync between environments in a domain
   */
  canSyncBetweenEnvironments(
    user: User | null,
    domainId: string,
    sourceEnv: Environment,
    targetEnv: Environment
  ): boolean {
    if (!user || !user.roles) return false;
    return permissionsService.canSyncBetweenEnvironments(user.roles, domainId, sourceEnv, targetEnv);
  }

  /**
   * Get user's role display name
   */
  getUserRole(user: User | null): string {
    if (!user || !user.roles) return 'No Role';
    return permissionsService.getUserRoleDisplay(user.roles);
  }

  /**
   * Get role description
   */
  getRoleDescription(user: User | null): string {
    const role = this.getUserRole(user);
    return permissionsService.getRoleDescription(role);
  }

  /**
   * Check if an API error is an access denied error
   */
  isAccessDeniedError(error: any): boolean {
    return error?.status === 403 || 
           (error?.response?.status === 403) ||
           (error?.message && error.message.includes('Access Denied'));
  }
}

export const authService = new AuthService();
export default authService;